import React, { useCallback, useEffect, useMemo, useState } from 'react';

interface StockImage {
  id: string;
  thumb: string;
  full: string;
  alt: string;
  provider: 'unsplash' | 'pexels';
  attribution: string;
  photographerUrl?: string;
  sortTimestamp: number;
  width: number;
  height: number;
}

interface StockImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (image: StockImage) => void;
}

const DEFAULT_QUERY = 'creative';

const sanitizeKey = (value?: string) => {
  if (!value) return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  const unquoted = trimmed.replace(/^['"]|['"]$/g, '');
  if (!unquoted) return '';
  return unquoted;
};

/** API keys for the stock photo providers (shared with the landing page builder's photo library). */
export const getUnsplashKey = () => sanitizeKey(process.env.REACT_APP_UNSPLASH_ACCESS_KEY) || 'HeD9O9UXgveho3plBB4kTmfFWJur3z6BCR8EusFpzZs';
export const getPexelsKey = () => sanitizeKey(process.env.REACT_APP_PEXELS_API_KEY);

const StockImageModal: React.FC<StockImageModalProps> = ({ isOpen, onClose, onSelect }) => {
  const [query, setQuery] = useState(DEFAULT_QUERY);
  const [pendingQuery, setPendingQuery] = useState(DEFAULT_QUERY);
  const [images, setImages] = useState<StockImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [providerFilter, setProviderFilter] = useState<'all' | 'unsplash' | 'pexels'>('all');
  const [orientationFilter, setOrientationFilter] = useState<'any' | 'landscape' | 'portrait' | 'square'>('any');
  const [sortOption, setSortOption] = useState<'relevance' | 'latest'>('relevance');

  const unsplashKey = getUnsplashKey();
  const pexelsKey = getPexelsKey();

  const providersAvailable = useMemo(
    () => ({
      unsplash: Boolean(unsplashKey),
      pexels: Boolean(pexelsKey),
    }),
    [pexelsKey, unsplashKey]
  );

  const fetchUnsplash = useCallback(
    async (term: string): Promise<StockImage[]> => {
      if (!unsplashKey) return [];

      const params = new URLSearchParams({
        query: term,
        per_page: '24',
        client_id: unsplashKey,
      });

      if (orientationFilter !== 'any') {
        params.append('orientation', orientationFilter === 'square' ? 'squarish' : orientationFilter);
      }

      if (sortOption === 'latest') {
        params.append('order_by', 'latest');
      }

      const response = await fetch(`https://api.unsplash.com/search/photos?${params.toString()}`);

      if (!response.ok) {
        throw new Error('Failed to fetch Unsplash images');
      }

      const data = await response.json();

      return (data.results || []).map((result: any) => ({
        id: `unsplash_${result.id}`,
        thumb: result.urls?.small || result.urls?.thumb || '',
        full: result.urls?.regular || result.urls?.full || '',
        alt: result.alt_description || result.description || 'Unsplash image',
        provider: 'unsplash' as const,
        attribution: `${result.user?.name || 'Unsplash'} / Unsplash`,
        photographerUrl: result.user?.links?.html,
        sortTimestamp: result.created_at ? new Date(result.created_at).getTime() : Date.now(),
        width: typeof result.width === 'number' ? result.width : 1,
        height: typeof result.height === 'number' ? result.height : 1,
      }));
    },
    [orientationFilter, sortOption, unsplashKey]
  );

  const fetchPexels = useCallback(
    async (term: string): Promise<StockImage[]> => {
      if (!pexelsKey) return [];

      const params = new URLSearchParams({
        query: term,
        per_page: '24',
      });

      if (orientationFilter !== 'any') {
        params.append('orientation', orientationFilter);
      }

      const response = await fetch(`https://api.pexels.com/v1/search?${params.toString()}`, {
        headers: {
          Authorization: pexelsKey,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch Pexels images');
      }

      const data = await response.json();

      return (data.photos || []).map((photo: any) => ({
        id: `pexels_${photo.id}`,
        thumb: photo.src?.medium || photo.src?.small || '',
        full: photo.src?.large2x || photo.src?.original || photo.src?.large || '',
        alt: photo.alt || 'Pexels image',
        provider: 'pexels' as const,
        attribution: `${photo.photographer || 'Pexels'} / Pexels`,
        photographerUrl: photo.photographer_url,
        sortTimestamp: photo.id || Date.now(),
        width: typeof photo.width === 'number' ? photo.width : 1,
        height: typeof photo.height === 'number' ? photo.height : 1,
      }));
    },
    [orientationFilter, pexelsKey]
  );


  const fetchImages = useCallback(
    async (term: string) => {
      if (!providersAvailable.unsplash && !providersAvailable.pexels) {
        setError('Stock image providers are not configured. Please add API keys.');
        setImages([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const shouldFetch = (provider: keyof typeof providersAvailable) =>
          providersAvailable[provider] && (providerFilter === 'all' || providerFilter === provider);

        const requests: Promise<StockImage[]>[] = [];

        if (shouldFetch('unsplash')) {
          requests.push(
            fetchUnsplash(term).catch((err) => {
              console.error(err);
              return [];
            })
          );
        }
        if (shouldFetch('pexels')) {
          requests.push(
            fetchPexels(term).catch((err) => {
              console.error(err);
              return [];
            })
          );
        }

        const responses = await Promise.all(requests);
        let combined = responses.flat();
        if (sortOption === 'latest') {
          combined = combined.sort((a, b) => b.sortTimestamp - a.sortTimestamp);
        }

        if (combined.length === 0) {
          setError('No assets found for that search.');
        }
        setImages(combined);
      } catch (err) {
        console.error(err);
        setError(err instanceof Error ? err.message : 'Failed to load stock assets');
        setImages([]);
      } finally {
        setLoading(false);
      }
    },
    [fetchPexels, fetchUnsplash, providerFilter, providersAvailable, sortOption]
  );

  useEffect(() => {
    if (isOpen) {
      setQuery(DEFAULT_QUERY);
      setPendingQuery(DEFAULT_QUERY);
      fetchImages(DEFAULT_QUERY);
    } else {
      setImages([]);
      setError(null);
    }
  }, [fetchImages, isOpen]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!pendingQuery.trim()) return;
    setQuery(pendingQuery.trim());
    fetchImages(pendingQuery.trim());
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100] flex items-center justify-center px-6 py-16">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[85vh] flex flex-col my-auto">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Stock Assets</h2>
            <p className="text-sm text-slate-500">
              Powered by {providersAvailable.unsplash && providersAvailable.pexels
                ? 'Unsplash & Pexels'
                : providersAvailable.unsplash
                  ? 'Unsplash'
                  : 'Pexels'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-2 text-slate-500 hover:bg-slate-100 transition-colors"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-5 border-b border-slate-200">
          <form onSubmit={handleSubmit} className="flex gap-3">
            <input
              type="text"
              value={pendingQuery}
              onChange={(e) => setPendingQuery(e.target.value)}
              placeholder="Search for brand, theme, mood..."
              className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
            />
            <button
              type="submit"
              className="px-5 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-medium hover:bg-black transition-colors"
              disabled={loading}
            >
              {loading ? 'Searching…' : 'Search'}
            </button>
          </form>
          <div className="mt-4 flex flex-wrap items-end gap-3 text-[13px]">
            <label className="flex flex-col gap-1 min-w-[180px]">
              <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Provider</span>
              <select
                value={providerFilter}
                onChange={(e) => setProviderFilter(e.target.value as any)}
                className="rounded-md border border-slate-200 px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-slate-400 text-sm bg-white"
              >
                <option value="all">All providers</option>
                {providersAvailable.unsplash && <option value="unsplash">Unsplash only</option>}
                {providersAvailable.pexels && <option value="pexels">Pexels only</option>}
              </select>
            </label>
            <label className="flex flex-col gap-1 min-w-[160px]">
              <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Orientation</span>
              <select
                value={orientationFilter}
                onChange={(e) => setOrientationFilter(e.target.value as any)}
                className="rounded-md border border-slate-200 px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-slate-400 text-sm bg-white"
              >
                <option value="any">Any</option>
                <option value="landscape">Landscape</option>
                <option value="portrait">Portrait</option>
                <option value="square">Square</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 min-w-[140px]">
              <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Sort</span>
              <select
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value as any)}
                className="rounded-md border border-slate-200 px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-slate-400 text-sm bg-white"
              >
                <option value="relevance">Best match</option>
                <option value="latest">Latest</option>
              </select>
            </label>
          </div>
          <p className="text-[13px] text-slate-400 mt-3">
            Images are free to use. Attribution is included automatically.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {error && (
            <div className="mb-4 rounded-xl bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center h-64 text-slate-500 text-sm">Loading images…</div>
          ) : images.length === 0 ? (
            <div className="flex items-center justify-center h-64 text-slate-500 text-sm">No images found.</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {images.map((image) => {
                const aspectRatio =
                  image.width > 0 && image.height > 0 ? `${image.width} / ${image.height}` : '4 / 3';
                return (
                  <button
                    key={image.id}
                    type="button"
                    className="relative group rounded-xl overflow-hidden border border-transparent hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-300"
                    onClick={() => onSelect(image)}
                  >
                    <div className="w-full bg-slate-100" style={{ aspectRatio }}>
                      <img src={image.thumb} alt={image.alt} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end text-white p-3">
                      <div className="text-xs uppercase tracking-wide">Photo</div>
                      <div className="text-[11px] uppercase tracking-wide opacity-80">{image.provider}</div>
                      <div className="text-sm font-medium line-clamp-1">{image.attribution}</div>
                      <div className="text-[11px] text-white/80">Click to use</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StockImageModal;


